import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../context/SessionContext';
import './CustomerAuthModal.css';

const VIEWS = {
  OPTIONS: 'options',
  GUEST: 'guest',
};

function CustomerAuthModal({ isOpen, onClose, onSuccess, restaurantId, organizationId }) {
  const { t } = useTranslation();
  const { updateSession } = useSession();
  const [view, setView] = useState(VIEWS.OPTIONS);
  const [loading, setLoading] = useState(false);
  const [guestName, setGuestName] = useState('');

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setView(VIEWS.OPTIONS);
      setGuestName('');
    }
  }, [isOpen]);

  const handleSocialLogin = (provider) => {
    setLoading(true);
    const returnUrl = window.location.href.split('#')[0];
    const apiUrl = process.env.REACT_APP_API_URL;
    window.location.href = `${apiUrl}/auth/social/${provider}/initiate?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const handleGuestContinue = async (e) => {
    e.preventDefault();
    setLoading(true);

    const guestData = {
      customerId: `guest_${Date.now()}`,
      name: guestName || t('auth.guest'),
      isGuest: true,
      restaurantId,
      organizationId,
    };

    updateSession({
      customer: guestData,
      isAuthenticated: false,
    });

    onSuccess?.(guestData);
    onClose();
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          <span className="material-icons" aria-hidden="true">close</span>
        </button>

        {/* Options View - Social Login */}
        {view === VIEWS.OPTIONS && (
          <div className="auth-options">
            <div className="auth-header">
              <span className="material-icons auth-icon" aria-hidden="true">person</span>
              <h2>{t('auth.welcomeTitle')}</h2>
              <p>{t('auth.welcomeSubtitle')}</p>
            </div>

            <div className="auth-benefits">
              <div className="benefit-item">
                <span className="material-icons" aria-hidden="true">history</span>
                <span>{t('auth.benefitHistory')}</span>
              </div>
              <div className="benefit-item">
                <span className="material-icons" aria-hidden="true">favorite</span>
                <span>{t('auth.benefitFavorites')}</span>
              </div>
              <div className="benefit-item">
                <span className="material-icons" aria-hidden="true">local_offer</span>
                <span>{t('auth.benefitOffers')}</span>
              </div>
            </div>

            <div className="auth-buttons">
              {/* Google Login */}
              <button
                className="auth-btn social google"
                onClick={() => handleSocialLogin('google')}
                disabled={loading}
              >
                <svg className="social-icon" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.continueWithGoogle')}
              </button>

              {/* Facebook Login — hidden until Meta app approval (NER-879) */}
              {process.env.REACT_APP_ENABLE_FACEBOOK_LOGIN === 'true' && (
                <button
                  className="auth-btn social facebook"
                  onClick={() => handleSocialLogin('facebook')}
                  disabled={loading}
                >
                  <svg className="social-icon" viewBox="0 0 24 24">
                    <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  {t('auth.continueWithFacebook')}
                </button>
              )}

              <div className="auth-divider">
                <span>{t('auth.or')}</span>
              </div>

              <button className="auth-btn text" onClick={() => setView(VIEWS.GUEST)}>
                {t('auth.continueAsGuest')}
              </button>
            </div>
          </div>
        )}

        {/* Guest View */}
        {view === VIEWS.GUEST && (
          <div className="auth-form-container">
            <button className="auth-back" onClick={() => setView(VIEWS.OPTIONS)}>
              <span className="material-icons" aria-hidden="true">arrow_back</span>
            </button>

            <h2>{t('auth.guestTitle')}</h2>
            <p className="auth-subtitle">{t('auth.guestSubtitle')}</p>

            <form onSubmit={handleGuestContinue} className="auth-form">
              <div className="form-group">
                <label>{t('auth.yourName')} <span className="optional">({t('common.optional')})</span></label>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder={t('auth.guestNamePlaceholder')}
                />
              </div>

              <button type="submit" className="auth-btn primary" disabled={loading}>
                {loading ? t('common.loading') : t('auth.continueToOrder')}
              </button>

              <div className="guest-note">
                <span className="material-icons" aria-hidden="true">info</span>
                <span>{t('auth.guestNote')}</span>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerAuthModal;
